import React, { Suspense, useContext } from "react";
import { BrowserRouter, Outlet, Route, Routes } from "react-router-dom";
import { AppContextProvider } from "./context/AppContext";
import { AuthContext, AuthProvider } from "./context/AuthContext";
import { I18nextProvider } from "react-i18next";
import i18n from "./config/i18n";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./assets/styles/custom.css";
import "./assets/styles/main.css";
import "./assets/styles/responsive.css";
import Loader from "./components/Loader";
import PrivateRoute from "./components/PrivateRoute";
import ScrollToTop from "./components/ScrollToTop";
import Home from "./pages/Auth/Home";
import Profile from "./pages/Auth/Profile";
import SignIn from "./pages/Auth/SignIn";
import Error from "./pages/Error";
import Forgot from "./pages/Auth/Forgot";
import Customer from "./pages/Customer";
import EmailTemplates from "./pages/EmailTemplates";
import Notification from "./pages/Notification";
import EmailView from "./pages/EmailTemplates/EmailView";
import Branch from "./pages/Branch";
import CompareBranch from "./pages/Branch/comparebranch";
import Employee from "./pages/Employee";
import Class from "./pages/Class";
import Content from "./pages/Content";
import Transaction from "./pages/Transaction";
import Subscription from "./pages/Subscription";
import SubscriptionRequest from "./pages/SubscriptionRequest";
import AttendanceManagement from "./pages/Attendance";
import AttendanceManagementView from "./pages/Attendance/view";
import Event from "./pages/Event/index";
import StaticContent from "./pages/StaticContent/Index";
import StaticContentUpdate from "./pages/StaticContent/Edit";
import StaticContentView from "./pages/StaticContent/View";
import Belt from "./pages/Belt";
import AllBelt from "./pages/Belt/AllBelt"
import Reports from "./pages/Report";
import EventView from "./pages/Event/EventView";
import Kyu from "./pages/Kyu/index";
import Booking from "./pages/Booking";
import Graduation from "./pages/Graduation";
import ExamAdmin from "./pages/Exam";
import ExamView from "./pages/Exam/View";
import CustomerViewPage from "./pages/Customer/CustomerView";
import ClassDetails from "./pages/Auth/classdetails";
import StudentView from "./pages/Auth/studentview";
import BranchView from "./pages/Branch/BranchView";
import StudentProgress from "./pages/StudentProgress";
import ClassViewPage from "./pages/Class/view";
import BannerIndex from "./pages/Banner";
import CityIndex from "./pages/City";
import ViewStudent from "./pages/Customer/ViewStudents";
import ClassAttendanceManager from "./pages/ClassAttendanceManager/index";
import ClassSlotList from "./pages/ClassAttendanceManager/ClassSlotList";
import SlotStudentList from "./pages/ClassAttendanceManager/SlotStudentList";
import PrivacyPublicPage from "./pages/PublicPage/PrivacyPage";
import ContactUsPage from "./pages/PublicPage/ContactUsPage";
import DojoDeletionPage from "./pages/PublicPage/DojoDeletionPage";

import dayjs from "dayjs";
import weekday from "dayjs/plugin/weekday";
import localeData from "dayjs/plugin/localeData";
import advancedFormat from "dayjs/plugin/advancedFormat";

dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(advancedFormat);

window.Buffer = window.Buffer || require("buffer").Buffer;

function App() {
  return (
    <AuthProvider>
      <AppContextProvider>
        <I18nextProvider i18n={i18n}>
          <Suspense fallback={<Loader />}>
            <BrowserRouter>
              <ScrollToTop />
              <ToastContainer closeOnClick={false} />
              <AppRoutes />
            </BrowserRouter>
          </Suspense>
        </I18nextProvider>
      </AppContextProvider>
    </AuthProvider>
  );
}

const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/login" element={<SignIn />} />
      <Route path="/forgot-password/:token" element={<Forgot />} />
      <Route path="/privacy-policy" element={<PrivacyPublicPage />} />
      <Route path="/contact-us" element={<ContactUsPage />} />
      <Route path="/dojo-deletion" element={<DojoDeletionPage />} />
      <Route path="/" element={<PrivateRoute> <Layout /> </PrivateRoute>} >
        <Route exact path="/" element={<Home />} />
        <Route exact path="/dashboard" element={<Home />} />
        <Route exact path="/profile" element={<Profile />} />
        <Route exact path="/students" element={<Customer />} />
        <Route path="/students/view/:id" element={<CustomerViewPage />} />
        <Route path="/students/parent/view/:id" element={<ViewStudent />} />
        <Route exact path="/branches" element={<Branch />} />
        <Route exact path="/branch/view/:id" element={<BranchView />} />
        <Route exact path="/class/:id" element={<ClassDetails />} />
        <Route exact path="/student/:id" element={<StudentView />} />
        <Route exact path="/branches/compare-branches" element={<CompareBranch />} />
        <Route exact path="/employees" element={<Employee />} />
        <Route exact path="/email-template" element={<EmailTemplates />} />
        <Route exact path="/email-view/:id" element={<EmailView />} />
        <Route exact path="/notification" element={<Notification />} />
        <Route exact path="/classes" element={<Class />} />
        <Route exact path="/classes/view/:id" element={<ClassViewPage />} />
        <Route exact path="/content" element={<Content />} />
        <Route exact path="/transactions" element={<Transaction />} />
        <Route exact path="/subscriptions" element={<Subscription />} />
        <Route exact path="/branch-belts/:id" element={<Belt />} />
        <Route exact path="/branch-belts" element={<AllBelt />} />
        <Route exact path="/subscriptionrequest" element={<SubscriptionRequest />} />
        <Route exact path="/event" element={<Event />} />
        <Route exact path="/event/view/:id" element={<EventView />} />
        <Route exact path="/attendence" element={<AttendanceManagement />} />
        <Route path="/attendence/view/:id" element={<AttendanceManagementView />} />
        <Route exact path="/reports" element={<Reports />} />
        <Route exact path="/static-content" element={<StaticContent />} />
        <Route exact path="/static-content/update/:slug" element={<StaticContentUpdate />} />
        <Route exact path="/static-content/view/:slug" element={<StaticContentView />} />
        <Route exact path="/kyu" element={<Kyu />} />
        <Route exact path="/booking" element={<Booking />} />
        <Route exact path="/graduation" element={<Graduation />} />
        <Route exact path="/exam" element={<ExamAdmin />} />
        <Route exact path="/exam/view/:id" element={<ExamView />} />
        <Route exact path="/student-progress" element={<StudentProgress />} />
        <Route exact path="/banner" element={<BannerIndex />} />
        <Route exact path="/cities" element={<CityIndex />} />
        <Route exact path="/classattendance-manager" element={<ClassAttendanceManager />} />
        <Route path="/class-attendance/:id" element={<ClassSlotList />} />
        <Route path="/slot-students/:classId/:slotId" element={<SlotStudentList />} />
      </Route>

      <Route path="*" element={<Error />} />
    </Routes>
  );
};

const Layout = () => {
  return (
    <>
      <Outlet />
    </>
  );
};

export default App;
